import { Component, type ReactNode } from 'react';
import { DatePicker } from '@blueprintjs/datetime';
import { Button, MenuTrigger, Popover, Separator } from '@launchpad-ui/components';
import moment from 'moment';

import './styles.scss';

type IDatePickerButtonProps = {
  children?: ReactNode;
  hidePresets?: boolean;
  onDateChange: (date: Date) => void;
  value?: Date | null;
  maxDate?: Date;
};

type IDatePickerButtonState = {
  value?: Date | null;
  isOpen: boolean;
};

export class DatePickerButton extends Component<IDatePickerButtonProps, IDatePickerButtonState> {
  constructor(props: IDatePickerButtonProps) {
    super(props);
    this.state = {
      value: props.value,
      isOpen: false,
    };
  }

  public componentDidUpdate(prevProps: IDatePickerButtonProps) {
    if (this.props.value !== prevProps.value) {
      this.setState({ value: this.props.value });
    }
  }

  public render() {
    const { children, hidePresets, maxDate, value: originalValue } = this.props;
    const { value, isOpen } = this.state;
    const isDirty = !moment.utc(value).isSame(moment.utc(originalValue));

    return (
      <div className="DatePickerButton-trigger">
        <MenuTrigger isOpen={isOpen} onOpenChange={this.handleOpenChange}>
          {children}

          <Popover className="DatePickerButton">
            <DatePicker value={value || undefined} maxDate={maxDate} onChange={this.handleDateChange} />

            {!hidePresets && (
              <>
                <Separator />
                <div className="DatePickerButton-presets-header">Presets</div>
                <div className="DatePickerButton-presets">
                  <Button variant="minimal" onPress={this.handleForward7Days}>
                    Add 7 days
                  </Button>
                  <Button variant="minimal" onPress={this.handleForward14Days}>
                    Add 14 days
                  </Button>
                </div>
              </>
            )}

            <div className="DatePickerButton-action">
              <Button variant="minimal" onPress={this.handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" isDisabled={!isDirty} onPress={this.handleSave}>
                Save
              </Button>
            </div>
          </Popover>
        </MenuTrigger>
      </div>
    );
  }

  private handleOpenChange = (isOpen: boolean) => {
    this.setState({
      isOpen,
      // Reset value to original when opening
      value: isOpen ? this.state.value : this.props.value,
    });
  };

  private handleCancel = () => {
    this.setState({
      isOpen: false,
      value: this.props.value,
    });
  };

  private handleForward7Days = () => {
    const { value } = this.state;
    const baseDate = value || new Date();
    const forward7Days = moment.utc(baseDate).add(7, 'days').toDate();
    this.setState({ value: forward7Days });
  };

  private handleForward14Days = () => {
    const { value } = this.state;
    const baseDate = value || new Date();
    const forward14Days = moment.utc(baseDate).add(14, 'days').toDate();
    this.setState({ value: forward14Days });
  };

  private handleDateChange = (date: Date, hasUserManuallySelectedDate: boolean) => {
    if (!date || !hasUserManuallySelectedDate) {
      return;
    }
    this.setState({ value: date });
  };

  private handleSave = () => {
    const { onDateChange } = this.props;
    const { value } = this.state;
    if (!value) {
      return;
    }
    this.setState({ isOpen: false });
    onDateChange(value);
  };
}
